import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Tooltip,
  Paper,
  Collapse,
  IconButton,
  Divider,
  TextField,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTemplate } from '../../context/TemplateContext';
import { useLayout } from '../../context/LayoutContext';
import { useDesign } from '../../context/DesignContext';
import { useTypography } from '../../context/TypographyContext';
import { useStyle } from '../../context/StyleContext';
import { useBackground } from '../../context/BackgroundContext';
import { useBarStyle } from '../../context/BarStyleContext';
import tokens from '../../theme/tokens';
import CollapsibleSectionHeader from '../shared/CollapsibleSectionHeader';
import { useLanguage } from '../../localization';

const TemplateSelectorComponent = () => {
  const { t } = useLanguage();
  const {
    allTemplates,
    applyTemplate,
    saveTemplate,
    deleteTemplate,
    currentEffectId,
  } = useTemplate();

  const { selectedLayout } = useLayout();
  const { selectedDesign, customColorScheme } = useDesign();
  const { selectedStyleId } = useStyle();
  const { selectedBackgroundId } = useBackground();
  const { selectedBarStyleId } = useBarStyle();
  const { typographySettings } = useTypography();

  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [openCategories, setOpenCategories] = useState({
    'Classic': true,
    'Classic Centered Timeline': true,
    'Sidebar Right Classic': true,
    'Sidebar Left Classic': true,
    'Sidebar Right Modern': true,
    'Sidebar Left Modern': true,
    'Topbar Classic': true,
    'Topbar Modern': true,
    'Tech': true,
    'Triangular': true,
    'Geometric': true,
  });

  const handleTemplateSelect = useCallback((template) => {
    applyTemplate(template);
  }, [applyTemplate]);

  const handleDeleteTemplate = useCallback((templateId, templateName) => {
    if (!window.confirm(`${t('settingsPanel.templates.deleteConfirm')} "${templateName || templateId}"?`)) {
      return;
    }
    deleteTemplate(templateId);
  }, [deleteTemplate, t]);

  const templatesByCategory = useMemo(() => {
    return allTemplates.reduce((acc, template) => {
      const category = template.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(template);
      return acc;
    }, {});
  }, [allTemplates]);

  const categoryOrder = ['Classic', 'Classic Centered Timeline', 'Sidebar Right Classic', 'Sidebar Right Modern', 'Sidebar Left Classic', 'Sidebar Left Modern', 'Topbar Classic', 'Topbar Modern', 'Tech', 'Triangular', 'Geometric', 'Other'];

  const toggleCategory = useCallback((category) => {
    setOpenCategories(prev => ({ ...prev, [category]: !prev[category] }));
  }, []);

  return (
    <Box sx={{ p: tokens.spacing[4] }}>
      <Typography
        sx={{
          mb: tokens.spacing[4],
          fontWeight: tokens.typography.fontWeights.medium,
          fontSize: tokens.typography.fontSizes.lg,
          color: tokens.colors.text.primary,
        }}
      >
        {t('settingsPanel.templates.title')}
      </Typography>
      {categoryOrder.map(category => {
        const templatesExist = !!templatesByCategory[category];
        if (!templatesExist && category !== 'Custom') {
          return null;
        }

        const hasSavedTemplatesInCategory = category === 'Custom' && templatesByCategory[category] && templatesByCategory[category].length > 0;

        return (
          <Box key={category} sx={{ mb: tokens.spacing[4] }}>
            {(templatesExist || category === 'Custom') && (
              <CollapsibleSectionHeader
                title={t(`templateCategories.${category}`) || category}
                isOpen={!!openCategories[category]}
                toggleOpen={() => toggleCategory(category)}
                isCategory={true}
              />
            )}
            <Collapse in={!!openCategories[category]}>
              {templatesExist && (
                <Grid container spacing={tokens.spacing[3]} sx={{ pt: tokens.spacing[2] }}>
                  {templatesByCategory[category].map((template) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
                      <Paper
                        elevation={1}
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          border: `1px solid ${tokens.colors.borders.default}`,
                          aspectRatio: '0.707', // A4 ratio (width/height)
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          transition: `all ${tokens.transitions.durations.quick} ease`,
                          backgroundColor: tokens.colors.background.paper,
                          borderRadius: tokens.radius.base,
                          overflow: 'hidden',
                          '&:hover': {
                            borderColor: tokens.colors.primary.light,
                            boxShadow: tokens.shadows.md,
                            backgroundColor: tokens.colors.neutral[50],
                            transform: 'translateY(-2px)',
                            '& .delete-template-button': {
                              opacity: 1,
                            }
                          }
                        }}
                      >
                        <Box
                          onClick={() => handleTemplateSelect(template)}
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: tokens.spacing[1]
                          }}
                        >
                          {template.thumbnail ? (
                            <img
                              src={template.thumbnail}
                              alt={`${template.name} Template`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: tokens.radius.sm,
                              }}
                            />
                          ) : (
                            <Box sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: tokens.colors.neutral[100],
                              borderRadius: tokens.radius.sm,
                              border: `1px solid ${tokens.colors.borders.light}`
                            }}>
                              <Typography variant="caption" color="textSecondary">{t('settingsPanel.templates.noPreview')}</Typography>
                            </Box>
                          )}
                        </Box>
                        {template.category === 'Custom' && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(template.id, template.name);
                            }}
                            aria-label={`Delete template ${template.name}`}
                            className="delete-template-button"
                            sx={{
                              position: 'absolute',
                              top: tokens.spacing[1],
                              right: tokens.spacing[1],
                              color: tokens.colors.error,
                              backgroundColor: tokens.colors.alpha.white[800],
                              opacity: 0,
                              transition: 'opacity 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: tokens.colors.alpha.white[900],
                                opacity: 1,
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

const TemplateSelector = React.memo(TemplateSelectorComponent);

export default TemplateSelector;